import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Icon,
  HStack,
  List,
  ListItem,
  Image,
  Flex,
  Badge,
  SimpleGrid,
  Card,
  Grid,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { Link as RouterLink } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import {
  Camera,
  Lightbulb,
  Clock,
  Database,
  Star,
  ArrowRight,
  Compass,
  Ruler,
  MagicWand,
  Binoculars,
  Target,
  GlobeHemisphereWest,
} from '@phosphor-icons/react'
import { useStats } from '../../hooks/useStats'
import { useAuth } from '../../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { useTexture } from '../../context/EnhancedTextureContext';

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// Image cache to prevent repeated loads
const imageCache = new Map();

export function ResearchToolsSection() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const stats = useStats();
  const { isTextureEnabled } = useTexture();
  
  // State for lazy loading
  const [isImageVisible, setIsImageVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const imageRef = useRef(null);
  const observerRef = useRef(null);

  const researchImage = '/textures/homepage/cuscomap.png';

  // Lazy loading and caching logic
  useEffect(() => {
    // Check if image is already cached
    if (imageCache.has(researchImage)) {
      setImageSrc(imageCache.get(researchImage));
      setImageLoaded(true);
      return;
    }

    // Set up intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isImageVisible) {
          setIsImageVisible(true);
          // Preload image (only in browser environment)
          if (typeof window !== 'undefined' && window.Image) {
            const img = new window.Image();
            img.onload = () => {
              setImageSrc(researchImage);
              setImageLoaded(true);
              // Cache the loaded image
              imageCache.set(researchImage, researchImage);
            };
            img.onerror = () => {
              console.warn('Failed to load image:', researchImage);
            };
            img.src = researchImage;
          } else {
            // Fallback for SSR or environments without Image constructor
            setImageSrc(researchImage);
            setImageLoaded(true);
          }
          
          // Disconnect observer after loading
          observerRef.current?.disconnect();
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of element is visible
        rootMargin: '50px' // Start loading 50px before element enters viewport
      }
    );

    if (imageRef.current) {
      observerRef.current.observe(imageRef.current);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [researchImage, isImageVisible]);

  const handleJoinCommunity = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/signup');
    }
  };

  const researchTools = [
    { 
      icon: Camera, 
      colorToken: 'interactive.primary', 
      text: 'Upload and analyze high-resolution photos and 3D models',
      tooltip: 'Supports JPEG, PNG, WebP, and GLB formats' 
    },
    { 
      icon: Lightbulb, 
      colorToken: 'accent.highlight', 
      text: 'AI-powered feature detection identifies key characteristics',
      tooltip: 'Using computer vision to identify stonework patterns' 
    },
    { 
      icon: Clock, 
      colorToken: 'accent.nordic', 
      text: 'Interactive timeline views show construction phases',
      tooltip: 'Visualize site development through the ages'
    },
    { 
      icon: Database, 
      colorToken: 'accent.celtic', 
      text: 'Cross-reference similar features across regions',
      tooltip: 'Find patterns between geographically distant sites'
    },
    { 
      icon: Star, 
      colorToken: 'accent.egyptian', 
      text: 'Track and validate astronomical alignments',
      tooltip: 'Solar, lunar and stellar alignment verification'
    },
  ];

  return (
    <Box
      bg="bg.surface" // This will be the very back background color
      py={{ base: 16, md: 20 }}
      position="relative"
      borderTop="1px solid"
      borderColor="border.light"
      overflow="hidden" // Important for pseudo-elements
      _before={{ // Leather texture - moved to outer Box
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: isTextureEnabled ? "url('/textures/leather.png')" : 'none',
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        opacity: isTextureEnabled ? useColorModeValue(0.1, 0.35) : 0,
        mixBlendMode: 'multiply', // Consider changing this if leather is too dark
        pointerEvents: 'none',
        zIndex: 0, // Behind Container, above Box's own bg
        transition: 'opacity 0.3s ease-in-out',
      }}
      _after={{ // Linen texture - moved to outer Box
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: isTextureEnabled ? "url('/textures/linen.png')" : 'none',
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        opacity: isTextureEnabled ? useColorModeValue(0.15, 0) : 0,
        mixBlendMode: 'overlay',
        pointerEvents: 'none',
        zIndex: 0, // Behind Container, above Box's own bg
        display: useColorModeValue('block', 'none'),
        transition: 'opacity 0.3s ease-in-out',
      }}
    >
      <Container
        variant="journal"
        maxW="container.xl"
        position="relative"
        bg={useColorModeValue("bg.surface", "bg.surface._dark")} 
        boxShadow="lg" 
        borderRadius="md" 
        zIndex={1} // Ensure container is above the Box's pseudo-elements
        // _before and _after pseudo-elements removed from here
      >
        {/* Decorative compass in top-right corner - remains child of Container */}
        <Box
          position="absolute"
          top="-10px"
          right="10px"
          width="60px"
          height="60px"
          opacity={0.4}
          display={{ base: 'none', md: 'block' }}
          zIndex={1} // Relative to Container, will be above Container's bg
          animation={`${rotate} 120s infinite linear`}
          sx={{
            '@media(prefers-reduced-motion: reduce)': {
              animation: 'none',
            }
          }}
        >
          <Icon as={Compass} width="100%" height="100%" color="border.heavy" />
        </Box>
        
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={12} alignItems="center">
          <VStack 
            spacing={8} 
            align={{ base: 'center', lg: 'flex-start' }} 
            textAlign={{ base: 'center', lg: 'left' }}
            animation={`${fadeIn} 0.8s forwards`}
            sx={{
              '@media(prefers-reduced-motion: reduce)': {
                animation: 'none',
              }
            }}
          >
            <VStack spacing={6} align={{ base: 'center', lg: 'flex-start' }}>
              <HStack spacing={2}>
                <Badge variant="journal">Cutting-Edge</Badge>
                <Badge variant="journal">Scientific</Badge>
                <Badge variant="journal">Technology</Badge>
              </HStack>
              
              <Heading 
                variant="journal" 
                size="2xl"
                position="relative"
                _after={{
                  content: '""',
                  position: 'absolute',
                  bottom: '-10px',
                  left: { base: '50%', lg: '0' },
                  transform: { base: 'translateX(-50%)', lg: 'translateX(0)' },
                  width: '60px',
                  height: '3px',
                  bg: 'accent.highlight',
                  borderRadius: 'full',
                  opacity: 0.7
                }}
              >
                Advanced Research Tools
              </Heading>
              
              <Text 
                fontSize="xl" 
                color="text.muted"
                fontStyle="italic"
                maxW="540px"
              >
                Our platform provides powerful features for documenting and analyzing megalithic sites with scientific precision.
              </Text>
            </VStack>

            <List spacing={5} width="100%">
              {researchTools.map((item, index) => (
                <ListItem 
                  key={index}
                  transition="all 0.3s"
                  _hover={{
                    transform: "translateX(5px)",
                  }}
                >
                  <Tooltip 
                    label={item.tooltip} 
                    placement="right" 
                    hasArrow 
                    bg="bg.accent" 
                    color="text.default"
                    fontFamily="handwritten"
                    fontSize="sm"
                  >
                    <HStack 
                      spacing={4} 
                      align="flex-start"
                      p={2}
                      borderRadius="md"
                      _hover={{
                        bg: useColorModeValue('rgba(255,255,255,0.5)', 'rgba(40,30,20,0.3)')
                      }}
                    >
                      <Flex
                        w="45px"
                        h="45px"
                        bg="bg.surface"
                        color={item.colorToken}
                        borderRadius="md"
                        justify="center"
                        align="center"
                        boxShadow="md"
                        flexShrink={0}
                        border="1px solid"
                        borderColor="border.default"
                        position="relative"
                        overflow="hidden"
                        _hover={{
                          boxShadow: "lg",
                          color: "interactive.hover",
                        }}
                        _before={{
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgImage: 'var(--texture-linen)',
                          bgRepeat: 'repeat',
                          bgSize: '30px',
                          opacity: 0.1,
                          pointerEvents: 'none',
                        }}
                      >
                        <Icon as={item.icon} fontSize="xl" weight="duotone" />
                      </Flex>
                      <Text fontFamily="body" fontWeight="medium" fontSize="md">{item.text}</Text>
                    </HStack>
                  </Tooltip>
                </ListItem>
              ))}
            </List>

            <VStack spacing={4} align={{ base: 'center', lg: 'flex-start' }} pt={6}>
              <Button
                onClick={handleJoinCommunity}
                size="lg"
                variant="artifact"
                w={{ base: 'full', sm: 'auto' }}
                rightIcon={<Icon as={ArrowRight} weight="bold" />}
                boxShadow="md"
                position="relative"
                _hover={{
                  transform: "translateY(-3px)",
                  boxShadow: "lg"
                }}
                animation={`${pulse} 5s infinite ease-in-out`}
                sx={{
                  '@media(prefers-reduced-motion: reduce)': {
                    animation: 'none',
                  }
                }}
              >
                Join the Research Community
              </Button>
              <Box 
                fontSize="sm" 
                color="text.muted"
                fontFamily="handwritten"
                textAlign={{ base: 'center', lg: 'left' }}
                maxW="300px"
              >
                Start contributing and earn reputation points and badges for your discoveries
              </Box>
            </VStack>
          </VStack>

          <Card
            variant="journal"
            p={6}
            position="relative"
            overflow="hidden"
            boxShadow="lg"
            borderWidth="1px"
            borderColor="border.default"
            _before={{
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgImage: 'var(--texture-canvas)',
              bgRepeat: 'repeat',
              bgSize: '200px',
              opacity: 0.05,
              pointerEvents: 'none',
              zIndex: 0 
            }}
          >
            <Box
              position="absolute"
              top="5px"
              left="5px"
              width="25px"
              height="25px"
              bgImage="url('/textures/celtic-motif.png')"
              bgSize="contain"
              bgPosition="center"
              bgRepeat="no-repeat"
              opacity={0.2}
              transform="rotate(0deg)"
            />
            <Box
              position="absolute"
              bottom="5px"
              right="5px"
              width="25px"
              height="25px"
              bgImage="url('/textures/egyptian-motif.png')"
              bgSize="contain"
              bgPosition="center"
              bgRepeat="no-repeat"
              opacity={0.2}
              transform="rotate(0deg)"
            />
            
            <VStack spacing={8}>
              <Box
                ref={imageRef}
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="md"
                border="1px solid"
                borderColor="border.light"
                bg={useColorModeValue('gray.50', 'gray.800')}
              >
                {imageLoaded && imageSrc ? (
                  <Image
                    src={imageSrc}
                    alt="Megalithic research - Cusco map"
                    borderRadius="md"
                    boxShadow="sm"
                    transition="all 0.5s ease"
                    opacity={1}
                    maxW="100%"
                    height="auto"
                    loading="lazy"
                  />
                ) : (
                  <Text color="text.muted" fontSize="sm">
                    {isImageVisible ? 'Loading image...' : ''}
                  </Text>
                )}
              </Box>

              {!stats.loading && (
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={6} width="full">
                  <VStack align="flex-start" spacing={2}>
                    <Flex
                      w="45px"
                      h="45px"
                      bg="bg.surface"
                      color="accent.nordic"
                      borderRadius="md"
                      justify="center"
                      align="center"
                      boxShadow="md"
                      border="1px solid"
                      borderColor="border.light"
                    >
                      <Icon as={Database} fontSize="xl" weight="duotone" />
                    </Flex>
                    <Heading size="sm" fontFamily="heading">
                      {stats.sitesCount.toLocaleString()}+ Sites
                    </Heading>
                    <Text fontSize="sm" color="text.muted" fontFamily="body">
                      Documented worldwide
                    </Text>
                  </VStack>

                  <VStack align="flex-start" spacing={2}>
                    <Flex
                      w="45px"
                      h="45px"
                      bg="bg.surface"
                      color="accent.mayan"
                      borderRadius="md"
                      justify="center"
                      align="center"
                      boxShadow="md"
                      border="1px solid"
                      borderColor="border.light"
                    >
                      <Icon as={Camera} fontSize="xl" weight="duotone" />
                    </Flex>
                    <Heading size="sm" fontFamily="heading">
                      {stats.mediaCount.toLocaleString()}+ Images
                    </Heading>
                    <Text fontSize="sm" color="text.muted" fontFamily="body">
                      High-resolution media
                    </Text>
                  </VStack>
                </SimpleGrid>
              )}
            </VStack>
          </Card>
        </Grid>
      </Container>
    </Box>
  );
}
